
import * as React from "react";
import type { Initiative, Person } from "@/lib/types";
import {
  TODAY, STATUS_KEYS, STATUS_LABELS,
  pctOfWindow, toDate, fmtDate, fmtDateLong, dateFromPct, snapToWeekStart, tupleFromDate, quarterBounds, quarterForDate,
  quarterLabel, quarterSegments, addMonths, sameMonth,
  DEFAULT_WINDOW_START, WINDOW_START_MIN, WINDOW_START_MAX, type TimeWindow,
} from "@/lib/constants";
import { rollingLabel, timelineWindow, zoomBounds, zoomLabel } from "@/lib/timeline-window";
import { Icon } from "./icons";
import { layoutBars, inWindow, MIN_BAR_WIDTH_PCT, type PlacedBar } from "@/lib/layout";
import { actualShipped, isOverdue } from "@/lib/baseline";
import { connectorPaths, dependentsOf } from "@/lib/dependencies";
import type { Scope } from "@/lib/hooks";
import { NO_WORKSTREAM, activeLevels, buildGroupTree, type GroupNode } from "@/lib/group-by";

// Drag-to-link: drag from the connector dot past a bar's right end onto
// another bar, and that bar now depends on this one ("this finishes, then
// that starts"). Lives at the Timeline level because a link can cross lanes.
type LinkDrag = { fromId: string; x0: number; y0: number; x: number; y: number; overId: string | null; valid: boolean };
type LinkApi = {
  drag: LinkDrag | null;
  start: (fromId: string, e: React.PointerEvent) => void;
  remove?: (dependent: Initiative) => void;
};
const LinkContext = React.createContext<LinkApi | null>(null);

const ROW_H = 36;
const LANE_PAD_TOP = 12;
const BAND_HEAD = 22; // caption above each band's bars
const BAND_GAP = 10;  // space before the rule that separates bands
const SUB_HEAD = 18;  // caption above a third-level block (e.g. a workstream in a pod)
const SUB_GAP = 4;    // space between third-level blocks
const DEP_POP_W = 280; // matches .dep-pop width
const DEP_POP_H = 110; // roughly, with a two-line name pair
const POP_GAP = 8;
const POP_MARGIN = 8;
const BAR_H = 28;     // .bar-row height — dependency connectors anchor to it

// Live pixel width of an element (dependency connectors are drawn in px,
// while bars are placed in %). Falls back to a one-off read where
// ResizeObserver isn't available.
function useElementWidth(ref: React.RefObject<HTMLElement | null>): number {
  const [w, setW] = React.useState(0);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(entries => setW(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

function StatusLegend() {
  return (
    <div className="tl-legend" role="note" aria-label="Status legend" data-tour="legend">
      {STATUS_KEYS.map(s => (
        <span key={s} className="tl-legend-item">
          <span className="tl-legend-dot" style={{ background: `var(--status-${s})` }} />
          {STATUS_LABELS[s]}
        </span>
      ))}
      <span className="tl-legend-item" title="Target date has passed and it isn't shipped, canceled, or on hold">
        <span className="tl-legend-dot" style={{ background: "var(--status-overdue)" }} />
        Overdue
      </span>
    </div>
  );
}

// Window stepper — the timeline is four whole quarters that opens on the
// current quarter. ‹ › slide it a quarter at a time; Today snaps back.
function WindowNav({ start, onChange }: { start: Date; onChange: (d: Date) => void }) {
  const atToday = sameMonth(start, DEFAULT_WINDOW_START);
  return (
    <div className="win-nav" role="group" aria-label="Timeline range">
      <button
        type="button"
        className="win-step"
        aria-label="Back one quarter"
        title="Back one quarter"
        disabled={start <= WINDOW_START_MIN}
        onClick={() => onChange(addMonths(start, -3))}
      >
        <Icon name="chevron-left" size={14} />
      </button>
      <button
        type="button"
        className="win-step"
        aria-label="Forward one quarter"
        title="Forward one quarter"
        disabled={start >= WINDOW_START_MAX}
        onClick={() => onChange(addMonths(start, 3))}
      >
        <Icon name="chevron-right" size={14} />
      </button>
      <span className="win-range" aria-live="polite">
        {rollingLabel(start)}
      </span>
      <button
        type="button"
        className="win-today"
        disabled={atToday}
        onClick={() => onChange(DEFAULT_WINDOW_START)}
        title="Jump back to the current quarter"
      >
        Today
      </button>
    </div>
  );
}

export function TimelineHeader({
  zoomed,
  win,
  onZoomQuarter,
}: {
  zoomed: boolean;
  win: TimeWindow;
  onZoomQuarter: (qId: string) => void;
}) {
  const todayVisible = TODAY >= win.start && TODAY < win.end;
  const todayPct = pctOfWindow(TODAY, win);
  // One cell per visible quarter, sized by its days in the window — so a
  // window opening mid-quarter starts on a narrow partial cell, and every
  // boundary lands exactly where the bars' day-based positions put it.
  // When zoomed, win is the picked quarters' bounds — whole cells.
  const segs = quarterSegments(win);
  const fr = (days: number[]) => days.map(d => `minmax(0, ${d}fr)`).join(" ");
  const sum = (days: number[]) => days.reduce((n, d) => n + d, 0);
  return (
    <div className="timeline-header">
      <div className="corner" />
      <div className="quarters" style={{ gridTemplateColumns: fr(segs.map(s => sum(s.days))) }}>
        {segs.map(({ q, months, days }) => (
          <div key={q.id} className="q-cell">
            {zoomed ? (
              <div className="q-label">{quarterLabel(q.id)}</div>
            ) : (
              // Clicking a quarter header zooms the axis to that quarter —
              // same effect as picking it alone in the Quarter filter.
              <button
                type="button"
                className="q-label q-label--zoomable"
                onClick={() => onZoomQuarter(q.id)}
                title={`Zoom to ${quarterLabel(q.id)}`}
              >
                {q.label}
                <span className="q-fy">{q.year}</span>
              </button>
            )}
            <div className="months" style={{ gridTemplateColumns: fr(days) }}>
              {months.map(m => <span key={m}>{m}</span>)}
            </div>
          </div>
        ))}
        {todayVisible && (
          <div className="today-tag" style={{ left: `${todayPct}%` }}>
            Today · {fmtDate(TODAY)}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- interactive bar ----------
//
// Three-date model gestures, gated by lifecycle:
//   right handle — drags the TARGET (re-estimate); the at-start estimate is
//                  preserved as originalTarget, so retro tracking survives.
//                  Available until shipped.
//   left handle  — the PLANNED start; only while planning (draft/not-started).
//                  Dragging it on a bar with no start yet creates one. The
//                  moment work begins (in-progress) the start is a fact and
//                  freezes — corrections happen in the drawer.
//   body drag    — while planning with a start set, shifts start + target
//                  together (whole plan slides); otherwise re-targets only.
//   shipped bars — locked: facts don't drag. Corrections happen in the drawer.
// The bar tracks the pointer continuously (day resolution) so the gesture
// feels 1:1; COMMITS snap to week starts (Mondays) — the drag tip previews
// the snapped dates that will actually land. One PATCH per gesture; Esc cancels.

type DragMode = "start" | "end" | "move"; // "end" = right edge = the target date

const MS_DAY = 86400000;

function addDays(d: Date, days: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
}

function clampDate(d: Date, lo: Date, hi: Date): Date {
  return d < lo ? lo : d > hi ? hi : d;
}

function TimelineBar({
  b,
  win,
  top,
  activeId,
  onBarClick,
  onUpdate,
  owner,
  onVerticalDrag,
  linkPair,
}: {
  b: PlacedBar;
  /** Shared by both bars of a drawn dependency (the dependent's id), so the
      What's new tour can spotlight a real linked pair. */
  linkPair?: string;
  /** Present when this bar can be dragged into another workstream block. */
  onVerticalDrag?: (phase: "move" | "drop" | "cancel", clientY: number) => void;
  win: TimeWindow;
  top: number;
  /** Owner's display name, shown in italics after the initiative name. */
  owner?: string;
  activeId: string | null;
  onBarClick: (b: PlacedBar) => void;
  onUpdate?: (id: string, patch: Partial<Initiative>) => void;
}) {
  const rowRef = React.useRef<HTMLDivElement>(null);
  const link = React.useContext(LinkContext);
  const linkRole = !link?.drag ? ""
    : link.drag.fromId === b.id ? "link-source"
    : link.drag.overId === b.id ? (link.drag.valid ? "link-ok" : "link-bad")
    : "";
  // Gesture source of truth lives in a ref, not state: commit on pointerup must
  // not depend on a re-render having happened between events (fast flicks can
  // deliver down/move/up inside one frame). State only mirrors it for painting.
  // axis: the first clear movement decides — mostly vertical (body only, when
  // workstream drags are offered) moves the bar between workstreams; anything
  // else is the date gesture.
  const ctx = React.useRef<{ mode: DragMode; pointerId: number; inert: boolean; originX: number; originY: number; width: number; dPct: number; dy: number; moved: boolean; axis: "x" | "y" | null } | null>(null);
  const [drag, setDrag] = React.useState<{ mode: DragMode; dPct: number; moved: boolean; dy: number } | null>(null);

  const winDays = Math.round((win.end.getTime() - win.start.getTime()) / MS_DAY);

  // Committed dates — same fallbacks the static layout uses (quarter bounds
  // for legacy rows that carry only a quarter). Right edge = shipped ?? target.
  const qb = quarterBounds(b.targetQ);
  const startDate = toDate(b.start) ?? toDate(b.target) ?? qb?.start ?? win.start;
  const endDate = toDate(actualShipped(b)) ?? toDate(b.target) ?? qb?.end ?? win.end;
  // Shipped/canceled are facts: no gestures. Decided by status alone, so a
  // stray ship date on an unshipped row can't freeze its bar.
  const locked = b.status === "shipped" || b.status === "canceled";
  const hasStarted = !!b.start;
  // While planning, the start is a movable intention. Once work begins the
  // start is a fact and only the target stays draggable.
  const planning = b.status === "draft" || b.status === "not-started";
  // Per-edge drag permissions: an edge drags only when the date it moves is
  // inside the visible window — a clipped far edge can't be represented on
  // this axis (edit it in the drawer, or zoom/switch to where it lives).
  const endInWin = endDate >= win.start && endDate < win.end;
  const canDragEnd = !locked && endInWin;
  const canDragStart = !locked && planning && !b._clipL;
  const canSlide = !locked && planning && !b._clipL && !b._clipR;

  // Where would this gesture land the span? Pure — used by both the live
  // preview (from state) and the commit (from the ref). With snap=false the
  // dates follow the pointer at day resolution (the bar feels glued to the
  // cursor); snap=true rounds to week starts — what actually commits.
  const project = React.useCallback((mode: DragMode, dPct: number, snap: boolean): { s: Date; e: Date } => {
    if (mode === "move") {
      const rawDays = (dPct / 100) * winDays;
      const days = snap ? Math.round(rawDays / 7) * 7 : Math.round(rawDays);
      if (!canSlide) {
        // The start is frozen (work began) or off-window — body drag
        // re-targets only, so the preview must hold the left edge still too.
        return { s: startDate, e: clampDate(addDays(endDate, days), startDate, win.end) };
      }
      let s = addDays(startDate, days);
      let e = addDays(endDate, days);
      // keep the span inside the visible window without squashing it
      if (s < win.start) { e = addDays(e, Math.round((win.start.getTime() - s.getTime()) / MS_DAY)); s = win.start; }
      if (e > win.end) { s = addDays(s, -Math.round((e.getTime() - win.end.getTime()) / MS_DAY)); e = win.end; }
      return { s, e };
    }
    if (mode === "start") {
      // No start yet: anchor at the bar's visual left edge (min-width inflated),
      // so the new planned start lands where the user actually dropped it —
      // not offset by the gap between the drawn edge and the target date.
      const anchor = hasStarted ? startDate : dateFromPct(b._left, win);
      const raw = dateFromPct(Math.max(0, Math.min(100, pctOfWindow(anchor, win) + dPct)), win);
      return { s: clampDate(snap ? snapToWeekStart(raw) : raw, win.start, endDate), e: endDate };
    }
    const raw = dateFromPct(Math.max(0, Math.min(100, pctOfWindow(endDate, win) + dPct)), win);
    return { s: startDate, e: clampDate(snap ? snapToWeekStart(raw) : raw, startDate, win.end) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, hasStarted, canSlide, b._left, win, winDays]);

  // Bar geometry follows the pointer; the tip shows what will actually commit.
  const lifting = !!drag && drag.dy !== 0;
  const preview = drag && drag.moved && !lifting ? project(drag.mode, drag.dPct, false) : null;
  const snapped = drag && drag.moved && !lifting ? project(drag.mode, drag.dPct, true) : null;

  // Esc cancels the gesture in flight.
  React.useEffect(() => {
    if (!drag) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (ctx.current?.axis === "y") onVerticalDrag?.("cancel", 0);
        ctx.current = null;
        setDrag(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag]);

  // Gestures start even without onUpdate — the click path (open drawer) runs
  // through pointerup; only commits are gated on onUpdate. Locked/clipped
  // bars start an INERT gesture: it still resolves clicks, but never shows a
  // drag preview or tip — a bar must not pretend to move when nothing can
  // commit.
  const begin = (mode: DragMode) => (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    if (ctx.current) return; // one gesture at a time — a second touch mid-drag must not re-base it
    e.stopPropagation();
    const canvas = rowRef.current?.parentElement; // .lane-canvas-inner
    const width = canvas?.getBoundingClientRect().width ?? 0;
    if (!width) return;
    try { rowRef.current?.setPointerCapture(e.pointerId); } catch { /* capture is best-effort */ }
    const inert =
      mode === "start" ? !canDragStart :
      mode === "end" ? !canDragEnd :
      !canSlide && !canDragEnd; // body drag needs a slide or at least a retarget
    ctx.current = { mode, pointerId: e.pointerId, inert, originX: e.clientX, originY: e.clientY, width, dPct: 0, dy: 0, moved: false, axis: null };
    setDrag({ mode, dPct: 0, moved: false, dy: 0 });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const c = ctx.current;
    if (!c || e.pointerId !== c.pointerId) return;
    const dx = e.clientX - c.originX;
    const dy = e.clientY - c.originY;
    if (!c.axis) {
      if (Math.abs(dx) <= 4 && Math.abs(dy) <= 6) return;
      c.axis = c.mode === "move" && onVerticalDrag && Math.abs(dy) > Math.abs(dx) ? "y" : "x";
    }
    // A body drag that started sideways can still become a workstream move:
    // once the pointer leaves the row by most of a row height, it's clearly
    // heading for another block. The date preview snaps back (nothing moves
    // in time) and the bar lifts instead.
    if (c.axis === "x" && c.mode === "move" && onVerticalDrag && Math.abs(dy) > ROW_H * 0.75) {
      c.axis = "y";
      c.dPct = 0;
    }
    if (c.axis === "y") {
      c.moved = true;
      c.dy = dy;
      setDrag({ mode: c.mode, dPct: 0, moved: true, dy });
      onVerticalDrag?.("move", e.clientY);
      return;
    }
    if (c.inert) return;
    c.dPct = (dx / c.width) * 100;
    c.moved = c.moved || Math.abs(dx) > 4;
    setDrag({ mode: c.mode, dPct: c.dPct, moved: c.moved, dy: 0 });
  };

  // pointercancel = the browser took the pointer away (touch scroll, alt-tab,
  // palm rejection). That is an abort, never a commit.
  const onPointerCancel = (e: React.PointerEvent) => {
    const c = ctx.current;
    if (!c || e.pointerId !== c.pointerId) return;
    if (c.axis === "y") onVerticalDrag?.("cancel", 0);
    ctx.current = null;
    setDrag(null);
  };

  const onPointerUp = (ev: React.PointerEvent) => {
    const c = ctx.current;
    if (c && ev.pointerId !== c.pointerId) return; // a different finger lifting must not end the gesture
    ctx.current = null;
    setDrag(null);
    if (!c) return;
    if (c.axis === "y") {
      onVerticalDrag?.("drop", ev.clientY);
      return;
    }
    // Pointer capture retargets the browser's click to the row, so the button's
    // onClick won't fire for mouse input — decide click-vs-drag right here.
    if (!c.moved) {
      if (c.mode === "move") onBarClick(b);
      return;
    }
    if (!onUpdate || locked || c.inert) return; // inert gestures resolve as clicks only
    const { s, e } = project(c.mode, c.dPct, true);
    // Right edge / body-drag re-estimate the target; the quarter follows it.
    const retarget: Partial<Initiative> = { target: tupleFromDate(e), targetQ: quarterForDate(e) ?? b.targetQ };
    const patch: Partial<Initiative> =
      c.mode === "start" ? { start: tupleFromDate(s) } :
      c.mode === "end" ? retarget :
      canSlide && hasStarted ? { start: tupleFromDate(s), ...retarget } : retarget;
    onUpdate(b.id, patch);
  };

  // Geometry: committed placement normally; while dragging, recompute from the
  // previewed dates with the SAME min-width inflation the layout uses — the bar
  // you grabbed must stay the size you grabbed it at (a compact bar slides,
  // it doesn't collapse to its true span).
  let left = b._left;
  let right = b._right;
  if (preview) {
    right = pctOfWindow(preview.e, win);
    left = Math.min(pctOfWindow(preview.s, win), right);
    if (right - left < MIN_BAR_WIDTH_PCT) left = Math.max(0, right - MIN_BAR_WIDTH_PCT);
  }
  const width = Math.max(2, right - left);

  const targetDate = toDate(b.target);
  const overdue = isOverdue(b);
  // The target tick only renders when the target itself is inside the visible
  // FY — a clipped bar's far-away target must not masquerade as the edge.
  const targetInWin = !!targetDate && targetDate >= win.start && targetDate < win.end;
  const targetPct = targetInWin && targetDate ? pctOfWindow(targetDate, win) : -1;
  const targetOffset = targetPct >= 0 ? ((targetPct - left) / width) * 100 : -1;

  return (
    <div
      ref={rowRef}
      className={`bar-row ${drag?.moved ? "is-adjusting" : ""} ${lifting ? "is-lifting" : ""} ${linkRole}`}
      style={{ left: `${left}%`, width: `${width}%`, top, transform: lifting ? `translateY(${drag!.dy}px)` : undefined }}
      data-link-pair={linkPair}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      <button
        type="button"
        className={`bar outlined s-${b.status} ${overdue ? "is-overdue" : ""} ${activeId === b.id ? "is-active" : ""} ${b._clipL ? "clip-l" : ""} ${b._clipR ? "clip-r" : ""}`}
        data-initiative-id={b.id}
        onPointerDown={begin("move")}
        // Mouse clicks are handled in onPointerUp (capture retargets click);
        // detail === 0 means keyboard activation (Enter/Space) — keep it working.
        onClick={(e) => { if (e.detail === 0) onBarClick(b); }}
        aria-label={`${b.name}${owner ? `, owner ${owner}` : ""}, status ${b.status}${overdue ? ", overdue" : ""}`}
        title={`${b.name}${owner ? ` — ${owner}` : ""}${targetDate ? ` — Target ${fmtDateLong(targetDate)}` : ""}${(b._clipL || b._clipR) ? " — continues past this view; off-screen dates edit in the drawer" : ""}`}
      >
        <span className="name-text">{b.name}{owner && <span className="bar-owner"> · {owner}</span>}</span>
        {targetOffset >= 0 && targetOffset <= 100 && (
          <span className="target-mark" style={{ left: `${targetOffset}%` }} />
        )}
      </button>
      {onUpdate && !locked && (
        <>
          {canDragStart && (
            <span
              className="bar-handle bar-handle--l"
              onPointerDown={begin("start")}
              title={hasStarted ? "Drag to move the planned start" : "Drag to set a planned start"}
              aria-hidden="true"
            />
          )}
          {canDragEnd && (
            <span
              className="bar-handle bar-handle--r"
              onPointerDown={begin("end")}
              title="Drag to re-estimate the target date"
              aria-hidden="true"
            />
          )}
        </>
      )}
      {onUpdate && link && (
        <span
          className="link-knob"
          onPointerDown={e => link.start(b.id, e)}
          title="Drag onto another initiative to make it depend on this one"
          aria-hidden="true"
        />
      )}
      {snapped && (
        <div className="drag-tip" role="status">
          {fmtDate(snapped.s)} – {fmtDate(snapped.e)}
          <span className="drag-tip-weeks">
            {Math.max(1, Math.round((snapped.e.getTime() - snapped.s.getTime()) / MS_DAY / 7))}w
          </span>
        </div>
      )}
    </div>
  );
}

// A lane's group tree flattened top to bottom: captions (depth 1 = bands,
// depth 2 = lighter blocks) and leaf groups, each packed on its own.
type Segment =
  | { kind: "caption"; key: string; depth: 1 | 2; name: string; count: number; rule: boolean }
  // ws: the leaf's workstream when it's a workstream group (null = "no
  // workstream"); undefined when workstream isn't the leaf's level. parent:
  // the group the leaf sits in — workstream drags stay within one parent.
  | { kind: "leaf"; key: string; items: Initiative[]; ws?: string | null; parent: string };

const leafWs = (n: GroupNode): string | null | undefined =>
  n.level !== "workstream" ? undefined : n.id === NO_WORKSTREAM ? null : n.name;

function laneSegments(node: GroupNode): Segment[] {
  if (!node.children) return [{ kind: "leaf", key: node.id, items: node.initiatives, ws: leafWs(node), parent: `lane:${node.id}` }];
  const out: Segment[] = [];
  for (const band of node.children) {
    if (!band.initiatives.length) continue;
    out.push({ kind: "caption", key: band.id, depth: 1, name: band.name, count: band.initiatives.length, rule: out.length > 0 });
    if (!band.children) {
      out.push({ kind: "leaf", key: band.id, items: band.initiatives, ws: leafWs(band), parent: `lane:${node.id}` });
      continue;
    }
    for (const block of band.children) {
      if (!block.initiatives.length) continue;
      const key = `${band.id}/${block.id}`;
      if (block.name) out.push({ kind: "caption", key, depth: 2, name: block.name, count: block.initiatives.length, rule: false });
      out.push({ kind: "leaf", key, items: block.initiatives, ws: leafWs(block), parent: band.id });
    }
  }
  return out;
}

function Lane({
  node,
  laneIndex,
  win,
  onBarClick,
  activeId,
  onUpdate,
  owners,
  onMoveWorkstream,
}: {
  node: GroupNode;
  /** Drop a bar into another workstream block (undefined = "no workstream"). */
  onMoveWorkstream?: (i: Initiative, workstream: string | undefined) => void;
  laneIndex: number;
  /** driId -> display name, for the italic owner after each bar's name. */
  owners: Map<string, string>;
  win: TimeWindow;
  onBarClick: (b: PlacedBar) => void;
  activeId: string | null;
  onUpdate?: (id: string, patch: Partial<Initiative>) => void;
}) {
  const todayVisible = TODAY >= win.start && TODAY < win.end;
  const todayPct = pctOfWindow(TODAY, win);
  // Lay out each leaf group on its own, then stack them under their
  // captions. _row is made lane-unique (the label-placement check below
  // compares bars on the same row); _group tells connectors which links
  // cross groups.
  const segments = laneSegments(node);
  const laneIds = new Set(node.initiatives.map(i => i.id));
  const captions: (Extract<Segment, { kind: "caption" }> & { top: number })[] = [];
  const placed: (PlacedBar & { _top: number; _group: string })[] = [];
  const leaves: { key: string; ws?: string | null; parent: string; top: number; bottom: number; count: number }[] = [];
  let y = LANE_PAD_TOP;
  let rowBase = 0;
  let captionTop: number | null = null;
  segments.forEach((seg, idx) => {
    if (seg.kind === "caption") {
      if (seg.rule) y += BAND_GAP;
      captions.push({ ...seg, top: y });
      captionTop = y;
      y += seg.depth === 1 ? (seg.name ? BAND_HEAD : 4) : SUB_HEAD;
      return;
    }
    // Items linked from another group in this lane go to the top rows.
    const leafIds = new Set(seg.items.map(i => i.id));
    const priority = new Set(seg.items
      .filter(i => i.dependsOnId && laneIds.has(i.dependsOnId) && !leafIds.has(i.dependsOnId))
      .map(i => i.id));
    const { placed: bars, rowCount } = layoutBars(seg.items, win, priority);
    for (const b of bars) placed.push({ ...b, _row: b._row + rowBase, _top: y + b._row * ROW_H, _group: seg.key });
    leaves.push({ key: seg.key, ws: seg.ws, parent: seg.parent, top: captionTop ?? y, bottom: y + rowCount * ROW_H, count: bars.length });
    captionTop = null;
    y += rowCount * ROW_H;
    rowBase += rowCount;
    const next = segments[idx + 1];
    if (next?.kind === "caption" && next.depth === 2) y += SUB_GAP;
  });
  // "Depends on" connectors between bars placed in this lane.
  const innerRef = React.useRef<HTMLDivElement>(null);

  // Click a dependency line to remove it.
  const link = React.useContext(LinkContext);
  const [depPop, setDepPop] = React.useState<{ id: string; x: number; y: number } | null>(null);
  React.useEffect(() => {
    if (!depPop) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setDepPop(null); };
    const onDown = (e: PointerEvent) => { if (!(e.target as HTMLElement).closest?.(".dep-pop")) setDepPop(null); };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onDown);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("pointerdown", onDown); };
  }, [depPop]);
  const popDependent = depPop ? placed.find(p => p.id === depPop.id) : undefined;
  const popPredecessor = popDependent ? placed.find(p => p.id === popDependent.dependsOnId) : undefined;

  // Workstream drag: a bar in a workstream block can be dropped on another
  // workstream block with the same parent (same pod, or same lane). Only the
  // workstream changes — never team or pod.
  const [wsDrag, setWsDrag] = React.useState<{ barId: string; over: string | null } | null>(null);
  const verticalDragFor = (b: PlacedBar & { _group: string }) => {
    const from = leaves.find(l => l.key === b._group);
    if (!onMoveWorkstream || !from || from.ws === undefined) return undefined;
    const targets = leaves.filter(l => l.parent === from.parent && l.ws !== undefined);
    if (targets.length < 2) return undefined;
    const leafAt = (clientY: number) => {
      const yy = clientY - (innerRef.current?.getBoundingClientRect().top ?? 0);
      const hit = targets.find(l => yy >= l.top - 6 && yy < l.bottom + 6);
      return hit && hit.key !== from.key ? hit : null;
    };
    return (phase: "move" | "drop" | "cancel", clientY: number) => {
      if (phase === "cancel") { setWsDrag(null); return; }
      const hit = leafAt(clientY);
      if (phase === "move") { setWsDrag({ barId: b.id, over: hit?.key ?? null }); return; }
      setWsDrag(null);
      if (hit) onMoveWorkstream(b, hit.ws ?? undefined);
    };
  };
  const canvasW = useElementWidth(innerRef);
  // The lane canvas clips its overflow, so the popover is kept inside it:
  // shifted in from the edges, and opened upward when there's no room below.
  const popStyle: React.CSSProperties | undefined = depPop ? (() => {
    const left = Math.max(POP_MARGIN, Math.min(depPop.x - DEP_POP_W / 2, canvasW - DEP_POP_W - POP_MARGIN));
    const laneH = innerRef.current?.clientHeight ?? 0;
    const openUp = depPop.y + POP_GAP + DEP_POP_H > laneH && depPop.y - POP_GAP - DEP_POP_H >= 0;
    return openUp ? { left, bottom: laneH - depPop.y + POP_GAP } : { left, top: depPop.y + POP_GAP };
  })() : undefined;
  const links = canvasW > 0 ? connectorPaths(placed, canvasW, BAR_H) : [];
  // Each drawn link tags its two bars with the dependent's id (a bar keeps its
  // first tag), for the tour's spotlight.
  const linkPairs = new Map<string, string>();
  for (const l of links) {
    const predecessorId = placed.find(p => p.id === l.id)?.dependsOnId;
    if (predecessorId && !linkPairs.has(predecessorId)) linkPairs.set(predecessorId, l.id);
    if (!linkPairs.has(l.id)) linkPairs.set(l.id, l.id);
  }
  // Unique across the document, even with more than one timeline mounted.
  const markerId = `dep-arrow-${React.useId().replace(/:/g, "")}`;
  // Interior quarter boundaries (the window edges need no line).
  const dividers = quarterSegments(win).map(s => s.q.start).filter(d => d > win.start);
  const minHeight = Math.max(96, y + 16);

  return (
    <div className={`lane ${node.initiatives.length === 0 ? "is-empty" : ""}`} style={{ minHeight }}>
      <div className="lane-label">
        <div className="lane-label-main">
          {node.eyebrow && <div className="eyebrow">{node.eyebrow}</div>}
          <div className="name">{node.name}</div>
          <div className="lane-count" aria-label={`${node.initiatives.length} initiatives`}>
            <span className="lane-count-num">{String(node.initiatives.length).padStart(2, "0")}</span>
            <span className="lane-count-label">{node.initiatives.length === 1 ? "initiative" : "initiatives"}</span>
          </div>
        </div>
      </div>
      <div className="lane-canvas">
        {dividers.map(d => <div key={d.getTime()} className="q-divider" style={{ left: `${pctOfWindow(d, win)}%` }} />)}
        {todayVisible && <div className="today-line" style={{ left: `${todayPct}%` }} />}
        <div className="lane-canvas-inner" ref={innerRef}>
          {leaves.filter(l => l.ws !== undefined && (l.ws || l.key === wsDrag?.over)).map(l => (
            <div
              key={`ws-${l.key}`}
              className={`ws-block ${l.ws ? "" : "is-none"} ${wsDrag?.over === l.key ? "is-drop-target" : ""}`}
              style={{ top: l.top - 3, height: l.bottom - l.top + 2 }}
              // The tour shows a named workstream holding several initiatives.
              data-ws-multi={l.ws && l.count > 1 ? "" : undefined}
            />
          ))}
          {links.length > 0 && (
            <svg className="dep-links" width="100%" height="100%" aria-hidden="true">
              <defs>
                <marker id={markerId} viewBox="0 0 6 6" refX="5.5" refY="3" markerWidth="6" markerHeight="6" orient="auto">
                  <path d="M0,0 L6,3 L0,6 z" />
                </marker>
              </defs>
              {links.map(l => <path key={l.id} d={l.d} className={l.cross ? "is-cross" : undefined} markerEnd={`url(#${markerId})`} />)}
              {link?.remove && links.map(l => (
                <path
                  key={`hit-${l.id}`}
                  d={l.d}
                  className="dep-hit"
                  onClick={e => {
                    const r = innerRef.current?.getBoundingClientRect();
                    if (r) setDepPop({ id: l.id, x: e.clientX - r.left, y: e.clientY - r.top });
                  }}
                />
              ))}
            </svg>
          )}
          {captions.map(c => (
            <React.Fragment key={c.key}>
              {c.rule && <div className="band-rule" style={{ top: c.top - BAND_GAP / 2 }} />}
              {c.name && (
                <div className={`band-label ${c.depth === 2 ? "band-label--sub" : ""}`} style={{ top: c.top }}>
                  {c.name} <span className="band-count">{c.count}</span>
                </div>
              )}
            </React.Fragment>
          ))}
          {placed.map(b => {
            // Every label stays inside its bar, truncated with an ellipsis
            // when it doesn't fit; the full name is in the bar's tooltip.
            const owner = owners.get(b.driId);
            return (
              <TimelineBar
                key={b.id}
                b={b}
                win={win}
                top={b._top}
                activeId={activeId}
                onBarClick={onBarClick}
                onUpdate={onUpdate}
                owner={owner}
                onVerticalDrag={verticalDragFor(b)}
                linkPair={linkPairs.get(b.id)}
              />
            );
          })}
          {depPop && popDependent && popPredecessor && link?.remove && (
            <div className="dep-pop" role="dialog" aria-label="Dependency" style={popStyle}>
              <div className="dep-pop-title">Remove dependency?</div>
              <div className="dep-pop-pair">{popPredecessor.name} → {popDependent.name}</div>
              <div className="dep-pop-actions">
                <button type="button" className="btn sm ghost" onClick={() => setDepPop(null)}>Cancel</button>
                <button type="button" className="btn sm danger" onClick={() => { link.remove!(popDependent); setDepPop(null); }}>Remove</button>
              </div>
            </div>
          )}
          {placed.length === 0 && (
            <div className="empty-lane-msg">No initiatives in this lane.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export function Timeline({
  initiatives,
  scope,
  windowStart,
  onWindowStartChange,
  zoomQuarters,
  onZoomQuarter,
  onExitZoom,
  onBarClick,
  activeId,
  onUpdate,
  roster = [],
  onMoveWorkstream,
  allInitiatives,
  onLinkDependency,
  onRemoveDependency,
}: {
  initiatives: Initiative[];
  /** Every active initiative — link drags check loops against the full set, not just what's filtered. */
  allInitiatives?: Initiative[];
  /** Drag-to-link: `to` now depends on `from`. */
  onLinkDependency?: (from: Initiative, to: Initiative) => void;
  /** Click a dependency line → remove: `dependent` no longer depends on anything. */
  onRemoveDependency?: (dependent: Initiative) => void;
  /** Drag a bar into another workstream block (undefined = "no workstream"). */
  onMoveWorkstream?: (i: Initiative, workstream: string | undefined) => void;
  /** People, for the owner name shown on each bar. */
  roster?: Person[];
  scope: Scope;
  /** First month of the rolling 12-month window. */
  windowStart: Date;
  onWindowStartChange: (d: Date) => void;
  /** Quarters picked in the Quarter filter; the view zooms to them. Empty = rolling window. */
  zoomQuarters: string[];
  onZoomQuarter: (qId: string) => void;
  onExitZoom: () => void;
  onBarClick: (i: Initiative) => void;
  activeId: string | null;
  onUpdate?: (id: string, patch: Partial<Initiative>) => void;
}) {
  // The visible axis: four quarters normally, exactly the picked
  // quarters when zoomed (one quarter's three months fill the canvas).
  const win = React.useMemo<TimeWindow>(
    () => timelineWindow(windowStart, zoomQuarters),
    [zoomQuarters, windowStart]
  );
  const zoomed = zoomBounds(zoomQuarters) !== null;
  // Every initiative renders exactly once in every mode — the old objective
  // lanes (which duplicated multi-objective initiatives) are gone; objectives
  // live on as tags in the filter bar.
  //
  // Lane membership (and counts) reflect what's visible in the window, so
  // the numbers match the bars on screen. Memoized — Timeline re-renders on
  // every parent keystroke, and each inWindow call allocates Dates.
  const owners = React.useMemo(() => new Map(roster.map(p => [p.id, p.name])), [roster]);
  const visible = React.useMemo(() => initiatives.filter(i => inWindow(i, win)), [initiatives, win]);
  // ---- drag-to-link
  const everyone = allInitiatives ?? initiatives;
  const [linkDrag, setLinkDrag] = React.useState<LinkDrag | null>(null);
  const linkRef = React.useRef<LinkDrag | null>(null);
  const resolveLink = React.useCallback((d: LinkDrag, x: number, y: number): LinkDrag => {
    const el = document.elementFromPoint(x, y)?.closest("[data-initiative-id]");
    const id = el?.getAttribute("data-initiative-id") ?? null;
    const overId = id && id !== d.fromId ? id : null;
    const to = overId ? everyone.find(i => i.id === overId) : undefined;
    // Not valid when it would close a loop (the source already depends on the
    // target, directly or transitively) or when the link already exists.
    const valid = !!to && to.dependsOnId !== d.fromId && !dependentsOf(to.id, everyone).has(d.fromId);
    return { ...d, x, y, overId, valid };
  }, [everyone]);
  React.useEffect(() => {
    if (!linkDrag) return;
    const move = (e: PointerEvent) => {
      if (!linkRef.current) return;
      linkRef.current = resolveLink(linkRef.current, e.clientX, e.clientY);
      setLinkDrag(linkRef.current);
    };
    const up = (e: PointerEvent) => {
      const d = linkRef.current ? resolveLink(linkRef.current, e.clientX, e.clientY) : null;
      linkRef.current = null;
      setLinkDrag(null);
      if (!d?.valid || !d.overId || !onLinkDependency) return;
      const from = everyone.find(i => i.id === d.fromId);
      const to = everyone.find(i => i.id === d.overId);
      if (from && to) onLinkDependency(from, to);
    };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") { linkRef.current = null; setLinkDrag(null); } };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("keydown", key);
    };
    // Re-subscribe only when a drag starts or ends, not on every move.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkDrag?.fromId]);
  const linkApi = React.useMemo<LinkApi | null>(() => onLinkDependency ? {
    drag: linkDrag,
    start: (fromId, e) => {
      if (e.button !== 0) return;
      e.stopPropagation();
      e.preventDefault();
      const d: LinkDrag = { fromId, x0: e.clientX, y0: e.clientY, x: e.clientX, y: e.clientY, overId: null, valid: false };
      linkRef.current = d;
      setLinkDrag(d);
    },
    remove: onRemoveDependency,
  } : null, [linkDrag, onLinkDependency, onRemoveDependency]);

  const levels = activeLevels(scope.groupBy);
  const levelsKey = levels.join(",");
  const lanes = React.useMemo(
    () => buildGroupTree(visible, levelsKey ? (levelsKey.split(",") as typeof levels) : []),
    [visible, levelsKey],
  );

  return (
    <LinkContext.Provider value={linkApi}>
    <div className={`timeline ${levels.length === 0 ? "is-flat" : ""} ${linkDrag ? "is-linking" : ""}`}>
      <div className="tl-legend-row">
        <StatusLegend />
        {zoomed ? (
          <div className="zoom-crumb">
            <button type="button" className="zoom-exit" onClick={onExitZoom}>
              ← Back to 4 quarters
            </button>
            <span className="zoom-label">{zoomLabel(zoomQuarters)}</span>
          </div>
        ) : (
          <WindowNav start={windowStart} onChange={onWindowStartChange} />
        )}
      </div>
      <TimelineHeader zoomed={zoomed} win={win} onZoomQuarter={onZoomQuarter} />
      {lanes.map((n, i) => (
        <Lane key={n.id} node={n} laneIndex={i} win={win} onBarClick={onBarClick as (b: PlacedBar) => void} activeId={activeId} onUpdate={onUpdate} owners={owners} onMoveWorkstream={onMoveWorkstream} />
      ))}
      {linkDrag && (
        <svg className="link-drag-line" aria-hidden="true">
          <line x1={linkDrag.x0} y1={linkDrag.y0} x2={linkDrag.x} y2={linkDrag.y} className={linkDrag.overId ? (linkDrag.valid ? "is-ok" : "is-bad") : ""} />
          <circle cx={linkDrag.x} cy={linkDrag.y} r={4} className={linkDrag.overId ? (linkDrag.valid ? "is-ok" : "is-bad") : ""} />
        </svg>
      )}
    </div>
    </LinkContext.Provider>
  );
}
